// CustomCard.jsx
import React from "react";
import PropTypes from "prop-types";
import { Card } from "antd";

/**
 * CustomCard
 * Modern container card:
 * - Uses AntD Card but removes default borders
 * - Tailwind header layout (icon + title + subtitle + extras)
 * - compact mode: smaller paddings
 * - children = card content
 *
 * Props:
 *  - icon: ReactNode          (left icon)
 *  - cardTitle: string/node   (main title)
 *  - subtitle: string         (optional subtitle)
 *  - extras: ReactNode        (right side actions)
 *  - compact: boolean
 */
const FlexibleCard = ({
  icon,
  cardTitle,
  subtitle,
  extras,
  compact = false,
  children,
  className = "",
  style = {},
  ...rest
}) => {
  const padding = compact ? "p-1" : "p-6";
  const titleSize = compact ? "text-base" : "text-lg";

  return (
    <Card
      bordered={false}
      bodyStyle={{ padding: 0 }} // We handle our own padding
      style={{
        borderRadius: 14,
        background: "white",
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
        marginBottom:10,
        ...style,
      }}
      className={`custom-card ${className}`}
      {...rest}
    >
      {/* Header */}
      <div className={`flex items-start justify-between ${padding}`}>
        <div className="flex items-start gap-3">
          {icon && (
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50">
              {icon}
            </div>
          )}

          <div>
            <div className={`${titleSize} font-semibold text-gray-900`}>
              {cardTitle}
            </div>

            {subtitle && (
              <div className="text-sm text-gray-500 mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>

        {extras ? (
          <div className="flex items-center gap-2">{extras}</div>
        ) : null}
      </div>

      {/* Separator */}
      <div className="border-t border-gray-100" />

      {/* Body */}
      <div className={`${padding} `}>
        {children}
      </div>
    </Card>
  );
};

FlexibleCard.propTypes = {
  icon: PropTypes.node,
  cardTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.string,
  extras: PropTypes.node,
  compact: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default FlexibleCard;
